import React from 'react';

import { AppleCard } from './AppleCard';
import { Highlight } from '../types';

interface CardsStackProps {
  cardsData: Highlight[];
  activeCardId: string | undefined;
  completedCards: Set<string>;
  isAutoSequencing: boolean;
  abortSignal?: AbortSignal;
  onCardClick: (cardId: string) => void;
  onAnimationComplete: (cardId: string) => void;
}

export const CardsStack: React.FC<CardsStackProps> = (props) => {
  const { cardsData, activeCardId, completedCards, isAutoSequencing, abortSignal, onCardClick, onAnimationComplete } =
    props;

  const containerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState<number>(288);

  React.useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };

    measure();

    // Re-measure on fonts loaded
    document.fonts.ready.then(measure);

    const resizeObserver = new ResizeObserver(measure);

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  const handleCardClick = React.useCallback(
    (cardId: string) => {
      if (isAutoSequencing) {
        return;
      }

      if (cardId === activeCardId) {
        return;
      }

      onCardClick(cardId);
    },
    [activeCardId, isAutoSequencing, onCardClick],
  );

  return (
    <div ref={containerRef} className={'highlights'}>
      {cardsData.map((card) => (
        <AppleCard
          key={card.id}
          data={card}
          isActive={activeCardId === card.id}
          isCompleted={completedCards.has(card.id)}
          containerWidth={containerWidth}
          signal={activeCardId === card.id ? abortSignal : undefined}
          onAnimationComplete={onAnimationComplete}
          onClick={() => handleCardClick(card.id)}
        />
      ))}
    </div>
  );
};
